import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, Download, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePortalAssessment, downloadAssessmentPdf } from '@/api/hooks/usePortal';

export function PortalAssessmentSubmittedPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: assessment, isLoading } = usePortalAssessment(id);
  const [downloading, setDownloading] = useState(false);

  async function handleDownload() {
    if (!assessment) return;
    setDownloading(true);
    try {
      await downloadAssessmentPdf(id!, assessment.vendor_name);
    } catch {
      toast.error('Download failed — please try again');
    } finally {
      setDownloading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-4 py-16">
        <Skeleton className="h-16 w-16 rounded-full" />
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-72" />
      </div>
    );
  }

  const submissionDate = assessment?.submitted_at
    ? new Date(assessment.submitted_at).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : null;

  return (
    <div className="flex flex-col items-center gap-6 py-12 px-4 max-w-md mx-auto text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-emerald/15">
        <CheckCircle2 className="h-10 w-10 text-accent-emerald" />
      </div>

      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-text-primary">Assessment Submitted</h1>
        <p className="text-sm text-text-secondary">
          Your assessment has been submitted to{' '}
          <span className="font-medium text-text-primary">{assessment?.tenant_name ?? 'your customer'}</span>.
          They will review your responses and contact you if anything additional is needed.
        </p>
      </div>

      {submissionDate && (
        <div className="rounded-lg border border-border-subtle bg-bg-surface px-4 py-3 w-full text-left">
          <p className="text-xs text-text-secondary">Submission date</p>
          <p className="text-sm font-medium text-text-primary mt-0.5">{submissionDate}</p>
        </div>
      )}

      <div className="flex flex-col gap-3 w-full">
        <Button
          variant="outline"
          onClick={handleDownload}
          disabled={downloading}
          className="w-full"
        >
          {downloading
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating PDF…</>
            : <><Download className="h-4 w-4 mr-2" />Download my responses (PDF)</>
          }
        </Button>

        <Button
          variant="ghost"
          onClick={() => navigate('/portal/dashboard')}
          className="w-full"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
    </div>
  );
}
